import { FC, useCallback, useEffect, useState } from 'react';
import { Siren, RefreshCw, ExternalLink, Radar, ChevronDown, ChevronUp } from 'lucide-react';
import { dispatch } from '../../dispatch/dispatch';
import { actions } from '../../dispatch';

// SKUs that ARE getting orders but whose Jumia listing is not live because of
// something on Jumia's side (QC rejected / deactivated / deleted / hidden /
// waiting for QC). Auto-Pilot skips them, so they lose sales silently.

type IssueType = 'qc_rejected' | 'deleted' | 'inactive' | 'hidden' | 'qc_pending' | 'missing';

interface Issue {
  productId: string; sku: string; productName: string | null; productImage: string | null; productUrl: string | null;
  storeId: string; storeName: string | null; productGroupName: string | null;
  issue: IssueType; issueLabel: string; severity: number; hint: string;
  listingStatus: string | null; qcStatus: string | null; visible: number; visibleUpdatedAt: string | null;
  checkedAt: string | null; fetchError: string | null; price: number | null; stock: number | null;
  orders: number; openOrders: number; lastOrderAt: string | null;
  autoPilot: { _id: string; enabled: boolean; phase: string; lastDecision: string | null } | null;
}
interface Data { items: Issue[]; byType: Partial<Record<IssueType, number>>; checked: number; days: number; since: string; computedAt: string; meta: Record<IssueType, { label: string; severity: number; hint: string }>; }

interface Props { token: string; storeId: string; flash?: (msg: string, kind?: 'ok' | 'bad') => void; onCount?: (n: number) => void; }

const ISSUE_CLS: Record<IssueType, string> = {
  qc_rejected: 'li__badge--red', deleted: 'li__badge--red', missing: 'li__badge--red',
  inactive: 'li__badge--amber', hidden: 'li__badge--amber', qc_pending: 'li__badge--blue',
};
const ISSUE_ORDER: IssueType[] = ['qc_rejected', 'deleted', 'missing', 'inactive', 'hidden', 'qc_pending'];

const ago = (d: string | null | undefined) => {
  if (!d) return 'never';
  const ms = Date.now() - new Date(d).getTime();
  const h = Math.floor(ms / 3600000);
  if (h < 1) return `${Math.max(1, Math.floor(ms / 60000))}m ago`;
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};
const fmtDate = (d: string | null | undefined) => (d ? new Date(d).toLocaleDateString(undefined, { day: '2-digit', month: 'short' }) : '—');

const ListingIssuesPanel: FC<Props> = ({ token, storeId, flash, onCount }) => {
  const [days, setDays] = useState<7 | 14 | 30>(14);
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<IssueType | 'all'>('all');
  const [rechecking, setRechecking] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r: Data = await dispatch(actions.getListingIssues, {}, token, { storeId, days });
      setData(r); setError(null); onCount?.((r.items || []).length);
    } catch (e: any) { setError(e?.response?.data?.error?.message || e.message); }
    finally { if (!silent) setLoading(false); }
  }, [token, storeId, days, onCount]);

  useEffect(() => { load(); }, [load]);

  const recheck = async (ids: string[]) => {
    if (!ids.length) return;
    setRechecking(prev => new Set([...Array.from(prev), ...ids]));
    try {
      const r = await dispatch(actions.recheckListingIssues, { productIds: ids }, token);
      const results: any[] = r?.results || [];
      const fixed = results.filter(x => x.ok && !x.issue).length;
      const failed = results.filter(x => !x.ok);
      if (failed.length) flash?.(`${failed.length} SKU(s) could not be checked: ${failed[0].error}`, 'bad');
      else flash?.(fixed ? `✅ ${fixed} listing(s) are live again on Jumia — removed from the list` : `Checked ${results.length} SKU(s) live on Jumia — status unchanged`);
      await load(true);
    } catch (e: any) { flash?.(e?.response?.data?.error?.message || e.message, 'bad'); }
    finally { setRechecking(prev => { const n = new Set(prev); ids.forEach(i => n.delete(i)); return n; }); }
  };

  const items = (data?.items || []).filter(i => filter === 'all' || i.issue === filter);
  const total = data?.items.length || 0;

  return (
    <>
      <div className="ap__hero">
        <div className="ap__hero-left">
          <div className={`ap__hero-icon${total ? ' li__hero-icon--alert' : ''}`}><Siren size={18} color="#fff" /></div>
          <div>
            <div className="ap__hero-title">Listing issues · selling SKUs that Jumia is not showing
              {total > 0 ? <span className="ap__status li__status--alert">{total} SKU{total > 1 ? 's' : ''} losing sales</span> : data ? <span className="ap__status ap__status--on">all clear</span> : null}
            </div>
            <div className="ap__hero-sub">
              SKUs with at least one order in the last <b>{days} days</b> whose Jumia listing is <b>rejected by quality control</b>, <b>deactivated / deleted</b>, <b>hidden while in stock</b> or <b>waiting for QC</b>.
              Auto-Pilot skips these, and customers cannot buy them — open each one in Seller Center to see why.
              Status comes from the hourly Jumia sync; use <b>Re-check live</b> for a fresh answer.
            </div>
          </div>
        </div>
        <div className="ap__hero-actions">
          <div className="ap__row" style={{ gap: 4 }}>
            {([7, 14, 30] as const).map(d => <button key={d} className={`ap__chip-btn${days === d ? ' ap__chip-btn--on' : ''}`} onClick={() => setDays(d)}>{d}d</button>)}
          </div>
          <button className="ap__btn ap__btn--ghost" onClick={() => load()} disabled={loading}><RefreshCw size={13} className={loading ? 'ap__spin' : ''} /> Refresh</button>
          <button className="ap__btn ap__btn--primary" disabled={!items.length || rechecking.size > 0} onClick={() => recheck(items.slice(0, 25).map(i => i.productId))} title="Live GET on Jumia for every listed SKU (max 25 per click)">
            <Radar size={13} /> Re-check all live{items.length > 25 ? ' (first 25)' : ''}
          </button>
        </div>
      </div>

      {error && <div className="ap__alert ap__alert--bad">{error}</div>}
      {/* 🔔 open alerts: good SKUs (2+ orders in 60 d, or a lead) that stopped being live — they close by themselves when fixed */}
      {(data as any)?.alerts?.length > 0 && (
        <div style={{ border: '1px solid #fecaca', background: '#fef2f2', borderRadius: 12, padding: 10, margin: '8px 0' }}>
          <div style={{ fontWeight: 800, fontSize: 13, color: '#b91c1c', marginBottom: 6 }}>🔔 Open alerts — {(data as any).alerts.length} good SKU(s) not live on Jumia (also sent to Telegram; they close by themselves once live again)</div>
          <table style={{ width: '100%', fontSize: 12 }}>
            <thead><tr style={{ textAlign: 'left', color: '#64748b' }}><th>Since</th><th>Problem</th><th>Store</th><th>SKU</th><th>Product</th><th style={{ textAlign: 'right' }}>Orders 60 d</th><th>Lead</th></tr></thead>
            <tbody>
              {(data as any).alerts.map((a: any) => (
                <tr key={a._id} style={{ borderTop: '1px solid #fee2e2' }}>
                  <td>{new Date(a.openedAt).toLocaleString('fr-MA', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                  <td style={{ fontWeight: 700, color: '#b91c1c' }} title={a.hint || ''}>{a.label}</td>
                  <td>{a.storeName}</td>
                  <td style={{ fontFamily: 'monospace' }}>{a.productUrl ? <a href={a.productUrl} target="_blank" rel="noreferrer">{a.sku}</a> : a.sku}</td>
                  <td>{a.groupName || a.productName}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{a.orders60}</td>
                  <td>{a.wasLead ? (a.newLeadSku ? `moved → ${a.newLeadSku}` : 'was lead — moves at next run') : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && (
        <div className="ap__kpis">
          <button className={`ap__kpi${filter === 'all' ? ' ap__kpi--on' : ''}`} onClick={() => setFilter('all')}><span>With issues</span><b className={total ? 'ap__c-red' : 'ap__c-green'}>{total}</b><small>of {data.checked} SKUs with orders</small></button>
          {ISSUE_ORDER.filter(t => data.byType[t]).map(t => (
            <button key={t} className={`ap__kpi${filter === t ? ' ap__kpi--on' : ''}`} onClick={() => setFilter(filter === t ? 'all' : t)}>
              <span>{data.meta?.[t]?.label || t}</span><b className={t === 'qc_pending' ? 'ap__c-blue' : ISSUE_CLS[t] === 'li__badge--red' ? 'ap__c-red' : 'ap__c-amber'}>{data.byType[t]}</b><small>{t === 'qc_rejected' ? 'fix & resubmit' : t === 'hidden' ? 'Jumia hides it' : t === 'qc_pending' ? 'not live yet' : 'check Seller Center'}</small>
            </button>
          ))}
        </div>
      )}

      <div className="ap__table-wrap">
        {loading && !data ? <div className="ap__empty">Checking listings…</div>
        : !items.length ? <div className="ap__empty">{data ? (total ? 'No SKU matches this filter.' : `✅ Every SKU that got an order in the last ${days} days is live on Jumia.`) : ''}</div>
        : (
          <table className="ap__table">
            <thead>
              <tr>
                <th>Product</th><th>Store</th><th>Problem</th><th>Jumia status</th><th>Orders ({days}d)</th><th>Price / stock</th><th>Auto-Pilot</th><th>Checked</th><th></th>
              </tr>
            </thead>
            <tbody>
              {items.map(i => {
                const busy = rechecking.has(i.productId);
                const isOpen = open === i.productId;
                return (
                  <FragmentRow key={i.productId} isOpen={isOpen}>
                    <tr className={i.severity >= 3 ? 'ap__tr--error' : 'ap__tr--pending'}>
                      <td>
                        <div className="ap__prod">
                          {i.productImage ? <img src={i.productImage} alt="" className="ap__thumb" /> : <div className="ap__thumb ap__thumb--empty" />}
                          <div>
                            <div className="ap__sku">{i.productUrl ? <a href={i.productUrl} target="_blank" rel="noreferrer">{i.sku}</a> : i.sku}</div>
                            <div className="ap__muted ap__ellipsis" title={i.productName || ''}>{i.productName || '—'}</div>
                            {i.productGroupName && <div className="ap__muted ap__ellipsis" style={{ fontSize: '0.62rem' }}>{i.productGroupName}</div>}
                          </div>
                        </div>
                      </td>
                      <td>{i.storeName || '—'}</td>
                      <td>
                        <span className={`li__badge ${ISSUE_CLS[i.issue]}`}>{i.issueLabel}</span>
                        <button className="ap__link" style={{ display: 'block', marginTop: 3 }} onClick={() => setOpen(isOpen ? null : i.productId)}>{isOpen ? <ChevronUp size={11} /> : <ChevronDown size={11} />} what to do</button>
                      </td>
                      <td className="ap__muted" style={{ fontSize: '0.68rem', lineHeight: 1.5 }}>
                        listing <b>{i.listingStatus || '?'}</b><br />QC <b>{i.qcStatus || '?'}</b><br />visible <b>{i.visible === 1 ? 'yes' : i.visible === 0 ? 'no' : '?'}</b>{i.visibleUpdatedAt && i.visible === 0 ? <span> since {fmtDate(i.visibleUpdatedAt)}</span> : null}
                        {i.fetchError && <><br /><span className="ap__bad">{i.fetchError}</span></>}
                      </td>
                      <td><b>{i.orders}</b>{i.openOrders > 0 && <span className="ap__muted"> · {i.openOrders} open</span>}<div className="ap__muted" style={{ fontSize: '0.66rem' }}>last {fmtDate(i.lastOrderAt)}</div></td>
                      <td>{i.price != null ? `${i.price} MAD` : '—'}<div className="ap__muted" style={{ fontSize: '0.66rem' }}>stock {i.stock ?? '?'}</div></td>
                      <td className="ap__muted ap__ellipsis" style={{ maxWidth: 220, fontSize: '0.66rem' }} title={i.autoPilot?.lastDecision || ''}>
                        {i.autoPilot ? (i.autoPilot.enabled ? (i.autoPilot.lastDecision || i.autoPilot.phase) : 'disabled') : 'not enrolled'}
                      </td>
                      <td className="ap__muted" title={i.checkedAt || ''}>{ago(i.checkedAt)}</td>
                      <td>
                        <div className="ap__row" style={{ gap: 4, flexWrap: 'nowrap' }}>
                          <button className="ap__btn ap__btn--sm ap__btn--ghost" disabled={busy} onClick={() => recheck([i.productId])} title="Fresh GET on Jumia for this SKU"><Radar size={11} className={busy ? 'ap__spin' : ''} /> Re-check</button>
                          {i.productUrl && <a className="ap__btn ap__btn--sm ap__btn--ghost" href={i.productUrl} target="_blank" rel="noreferrer" title="Open the product page on Jumia"><ExternalLink size={11} /></a>}
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="ap__tr--detail">
                        <td colSpan={9}><div className="ap__detail li__hint"><b>{i.issueLabel}:</b> {i.hint}</div></td>
                      </tr>
                    )}
                  </FragmentRow>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {data && <div className="ap__muted" style={{ fontSize: '0.66rem', marginTop: 6 }}>Computed {ago(data.computedAt)} · orders since {fmtDate(data.since)} · {data.checked} SKUs with orders checked</div>}
    </>
  );
};

// tiny helper so a row + its detail row share one key
const FragmentRow: FC<{ isOpen: boolean; children: any }> = ({ children }) => <>{children}</>;

export default ListingIssuesPanel;
